import { brand } from "@pervasivemind/bi-components/brand";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Tabs,
  type TabItem,
} from "@omnitok/ui";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  FolderOpen,
  Inbox,
  Plus,
  Users,
  RefreshCw,
  Save,
} from "lucide-react";
import {
  DataTable,
  EmptyState,
  Section,
  StatusChip,
  resolveStatus,
  type Column,
} from "@pervasivemind/admin-ui";
import { Spinner } from "@pervasivemind/bi-components";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/i18n";
import {
  adminPermissionsApi,
  type ProjectAccessResponse,
} from "@/lib/admin-users-api";
import { draftsApi, projectsApi } from "@/lib/builder-api";
import { DeliverySubscriptionsPanel } from "@/pages/delivery/DeliverySubscriptionsPanel";
import { LoaderScheduleFields } from "@/pages/loader/LoaderScheduleFields";
import {
  ArchivosConfigFields,
  GeneralFields,
  MaestrosConfigFields,
  ProjectDataFields,
  SLUG_PATTERN,
  SectionToggles,
} from "@/pages/ProjectsPage";
import { findIconComponent } from "@/lib/project-icons";
import {
  hasProjectName,
  parseProjectName,
  serializeProjectName,
} from "@/lib/project-name";
import { CreateDraftModal } from "@/pages/projects/CreateDraftModal";
import { DraftsTable } from "@/pages/projects/DraftsTable";
import { ProjectConnectionsPanel } from "@/pages/projects/ProjectConnectionsPanel";
import { ProjectDimensionsPane } from "@/pages/projects/ProjectDimensionsPane";
import { ProjectIAPane } from "@/pages/projects/ProjectIAPane";
import { AiHomeTimeEditor } from "@/pages/builder/ai/AiHomeTimeSection";
import { ProjectEmbedPane } from "@/pages/projects/ProjectEmbedPane";
import { ProjectModeCard } from "@/pages/projects/ProjectModeCard";
import { UNASSIGNED_SLUG } from "@/pages/projects/ProjectsHomePage";
import { resolveLocalized } from "@pervasivemind/dashboard-render";
import type {
  DisplayLabel,
  FilesConfig,
  MasterFilesConfig,
  Project,
  ReportDraft,
} from "@/types";

type HubTab =
  | "reports"
  | "ia"
  | "data"
  | "loader"
  | "delivery"
  | "embed"
  | "access"
  | "config";

const VALID_TABS: ReadonlyArray<HubTab> = [
  "reports",
  "ia",
  "data",
  "loader",
  "delivery",
  "embed",
  "access",
  "config",
];

/**
 * Tabs only admins may open (`ia` PR-4, `access` PR-3, `embed` B3).
 *
 * `embed` is admin-gated in the UI even though the API accepts the
 * `manage_ai` flag: issuing an embed key opens an ANONYMOUS surface on
 * the public internet, so the affordance stays behind the narrower gate
 * while the backend keeps the broader one (a `manage_ai` holder calling
 * the endpoint directly is still authorized — deliberately).
 */
const ADMIN_ONLY_TABS: ReadonlyArray<HubTab> = ["ia", "access", "embed"];

/** Sub-views of the `data` pane (Plan J, J2). */
export type DataView = "source" | "dimensions";

const DATA_VIEWS: ReadonlyArray<DataView> = ["source", "dimensions"];

/**
 * An unknown or absent `?data=` is the source form. Exported for the
 * test — a hand-typed URL must not blank the pane, and the value now
 * arrives from something a person can type.
 */
export function resolveDataView(raw: string | null): DataView {
  return DATA_VIEWS.includes(raw as DataView) ? (raw as DataView) : "source";
}

/**
 * Project hub — the project is the organizing unit (PR-0 builder
 * reorg). Everything that belongs to a project lives behind one URL:
 *
 *   /projects/:slug?tab=reports|data|loader|config
 *
 * - Reportes: the project's report list (extracted ``DraftsTable``)
 *   plus the create-report modal with the project pre-selected.
 * - Datos / Auto-refresh: the existing ``ProjectDataFields`` /
 *   ``LoaderScheduleFields`` sub-forms, reused verbatim.
 * - Configuración: General + Maestros + Archivos stacked on one page
 *   with a single save (replaces the per-tab split of the retired
 *   ``ProjectEditPage`` edit mode — create mode keeps that page).
 *
 * The pseudo-slug ``_unassigned`` renders a reduced hub (reports tab
 * only) for drafts without a project.
 */
export function ProjectHubPage(): JSX.Element {
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, locale } = useT();
  const { user } = useAuth();

  const isUnassigned = routeSlug === UNASSIGNED_SLUG;
  // PR-3 builder reorg: the "Accesos" tab (who sees this project) is
  // admin-only — the backend route is admin-gated anyway.
  const isAdmin = user?.role === "admin";

  const [project, setProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [drafts, setDrafts] = useState<ReportDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped by the connections panel so the data-source picker below
  // it re-reads the list in the same breath the operator created the
  // row — before this it stayed on "(none)" until a page reload.
  const [connectionsVersion, setConnectionsVersion] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!routeSlug) return;
    setLoading(true);
    setError(null);
    try {
      const [draftsRes, projectsRes, fetched] = await Promise.all([
        draftsApi.list(),
        projectsApi.list(),
        isUnassigned ? Promise.resolve(null) : projectsApi.get(routeSlug),
      ]);
      setDrafts(draftsRes.drafts);
      setProjects(projectsRes.projects);
      setProject(fetched);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [routeSlug, isUnassigned, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Tab state mirrored to ?tab= (deep-linkable) ─────────────
  const rawTab = searchParams.get("tab") as HubTab | null;
  const activeTab: HubTab =
    rawTab &&
    VALID_TABS.includes(rawTab) &&
    !isUnassigned &&
    (!ADMIN_ONLY_TABS.includes(rawTab) || isAdmin)
      ? rawTab
      : "reports";
  const setActiveTab = (tab: HubTab): void => {
    setSearchParams(tab === "reports" ? {} : { tab }, { replace: true });
  };

  // ── Datos sub-view mirrored to ?data= (Plan J, J2) ───────────
  // Dimensiones is a SUB-view of `data`, not a ninth hub tab: it is a
  // property of the project's DATA, and the hub strip is already eight
  // items wide. Guarded by `edit_projects` on the backend, the same gate
  // as `PUT /data-source`, so it is deliberately NOT in ADMIN_ONLY_TABS
  // and not wrapped in `isAdmin`.
  const dataView = resolveDataView(searchParams.get("data"));
  const setDataView = useCallback(
    (next: DataView): void => {
      // CLONE, never rebuild. `setActiveTab` above writes a FRESH param
      // set, so building one here would drop `tab=data`, the ternary at
      // `activeTab` would fall back to "reports", and clicking the
      // sub-tab would throw the analyst out of the pane entirely.
      // ProjectIAPane already solved this for `?ia=` the same way.
      const params = new URLSearchParams(searchParams);
      if (next === "source") params.delete("data");
      else params.set("data", next);
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );

  // Sorted the same way the PORTAL sidebar sorts (order_index, key) —
  // before the DnD feature this table listed by updated_at DESC, which
  // showed a DIFFERENT order than what portal users actually see.
  const projectDrafts = useMemo(
    () =>
      (isUnassigned
        ? drafts.filter((d) => !d.project_id)
        : drafts.filter((d) => project && d.project_id === project.id)
      )
        .slice()
        .sort(
          (a, b) =>
            (a.order_index ?? 0) - (b.order_index ?? 0) ||
            a.key.localeCompare(b.key),
        ),
    [drafts, project, isUnassigned],
  );

  const publishedCount = projectDrafts.filter(
    (d) => d.status === "published",
  ).length;

  const handleDelete = async (draft: ReportDraft): Promise<void> => {
    if (!window.confirm(t("list.delete.confirm"))) return;
    setBusyKey(draft.key);
    try {
      await draftsApi.remove(draft.key);
      setDrafts((current) => current.filter((d) => d.key !== draft.key));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setBusyKey(null);
    }
  };

  const handleCreated = (draft: ReportDraft): void => {
    setCreateOpen(false);
    // Created from a template with tabs: the server split it into a group
    // (PR-3), so the group's page opens, not one member's editor.
    if (draft.group && project) {
      navigate(
        `/projects/${encodeURIComponent(project.slug)}/groups/${encodeURIComponent(draft.group.key)}`,
      );
      return;
    }
    navigate(`/builder/${encodeURIComponent(draft.key)}`);
  };

  // ── Sidebar order DnD (2026-07-20) ───────────────────────────
  // Optimistic: apply the new order locally the moment the row is
  // dropped, then persist. On failure restore the pre-drop snapshot
  // and surface the error — no silent divergence from the server.
  const [orderFeedback, setOrderFeedback] = useState<
    "saved" | "error" | null
  >(null);

  const handleReorder = async (orderedKeys: string[]): Promise<void> => {
    if (!project) return;
    const snapshot = drafts;
    const position = new Map(orderedKeys.map((k, i) => [k, i]));
    setOrderFeedback(null);
    setDrafts((current) =>
      current.map((d) =>
        position.has(d.key)
          ? { ...d, order_index: position.get(d.key)! }
          : d,
      ),
    );
    try {
      await projectsApi.reorderReports(project.slug, orderedKeys);
      setOrderFeedback("saved");
    } catch {
      setDrafts(snapshot);
      setOrderFeedback("error");
    }
    setTimeout(() => setOrderFeedback(null), 2500);
  };

  const resolvedName = isUnassigned
    ? t("home.unassigned")
    : project
      ? resolveLocalized(project.name as DisplayLabel, locale) || project.slug
      : "";
  const HeaderIcon = isUnassigned
    ? Inbox
    : (findIconComponent(project?.icon) ?? FolderOpen);

  const tabs: TabItem[] = isUnassigned
    ? [{ id: "reports", label: t("hub.tab.reports") }]
    : [
        { id: "reports", label: t("hub.tab.reports") },
        // PR-4: project-level AI assets (context notes + knowledge) —
        // admin-only, same guard as Accesos.
        ...(isAdmin ? [{ id: "ia", label: t("hub.tab.ia") }] : []),
        { id: "data", label: t("projects.form.tab.data") },
        { id: "loader", label: t("projects.form.tab.loader") },
        // Track B2 — scheduled delivery of this project's insights.
        { id: "delivery", label: t("hub.tab.delivery") },
        // Track B3 — embed keys for third-party sites (admin-only).
        ...(isAdmin ? [{ id: "embed", label: t("hub.tab.embed") }] : []),
        ...(isAdmin
          ? [{ id: "access", label: t("hub.tab.access") }]
          : []),
        { id: "config", label: t("hub.tab.config") },
      ];

  if (loading && !project && drafts.length === 0) {
    return (
      <div className="flex h-[70vh] items-center justify-center text-sm text-neutral-500">
        <Spinner size={16} className="mr-2" ariaLabel={t("common.loading")} />
        {t("common.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto mt-12 max-w-md px-6">
        <Card variant="bordered" className="border-error/40 bg-error/5">
          <CardBody>
            <div className="flex items-start gap-3 text-sm text-error-dark">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">{error}</p>
                <Button
                  variant="danger"
                  ghost
                  size="xs"
                  onClick={() => void load()}
                  className="mt-3"
                >
                  {t("common.retry")}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} /> {t("hub.back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* ── Header ─────────────────────────────────────────────
          Look v3: on the canvas, no white band, the title in the
          operator-page head (admin-ui PageHeader). It wraps on a narrow
          screen instead of squeezing the name to nothing. */}
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 pb-3 pt-6 sm:px-8">
        <div className="min-w-0 flex-[1_1_20rem]">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900"
          >
            <ArrowLeft size={12} /> {t("hub.back")}
          </Link>
          <div className="mt-2 flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary-ink">
              <HeaderIcon size={18} />
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                <h1 className="min-w-0 font-display text-[25px] font-extrabold leading-[1.25] tracking-[-0.5px] text-neutral-800">
                  {resolvedName}
                </h1>
                {!isUnassigned && project && (
                  <span className="shrink-0 font-mono text-xs text-neutral-500">
                    {project.slug}
                  </span>
                )}
                <Badge variant="neutral" size="sm" pill className="shrink-0">
                  {projectDrafts.length} · {publishedCount}{" "}
                  {t("home.published")}
                </Badge>
              </div>
              {!isUnassigned && project?.description && (
                <p className="mt-[3px] max-w-2xl text-xs leading-[1.6] text-neutral-500">
                  {project.description}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:pt-6">
          <Button
            ghost
            size="sm"
            onClick={() => void load()}
            disabled={loading}
            leftIcon={
              <RefreshCw
                size={14}
                className={loading ? "animate-spin" : undefined}
              />
            }
          >
            <span className="sr-only">{t("common.refresh")}</span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setCreateOpen(true)}
          >
            {t("list.addReport")}
          </Button>
        </div>
      </div>

      <div className="border-b border-neutral-200 px-4 sm:px-8">
        <Tabs
          items={tabs}
          activeId={activeTab}
          onChange={(id: string) => setActiveTab(id as HubTab)}
          variant="underline"
          size="sm"
        />
      </div>

      {/* ── Panes ──────────────────────────────────────────────
          Mismo contenedor que el resto de las páginas del builder
          (Operations / Users / Panorama): max-w-6xl, py-8, fondo
          default — sin la "isla" gris angosta (feedback 2026-07-17). */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8">
          {activeTab === "reports" && (
            <div className="space-y-2">
              {orderFeedback === "saved" && (
                <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-success/10 px-4 py-2 text-sm text-success-dark">
                  <Check size={14} /> {t("list.reorderSaved")}
                </div>
              )}
              {orderFeedback === "error" && (
                <div className="flex items-center gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
                  <AlertCircle size={14} /> {t("list.reorderError")}
                </div>
              )}
              <Card variant="bordered" noPadding>
                {projectDrafts.length > 0 ? (
                  <DraftsTable
                    drafts={projectDrafts}
                    busyKey={busyKey}
                    onDelete={(d) => void handleDelete(d)}
                    onReorder={
                      !isUnassigned && projectDrafts.length > 1
                        ? (keys) => void handleReorder(keys)
                        : undefined
                    }
                  />
                ) : (
                  <div className="px-4 py-10 text-center text-sm text-neutral-500">
                    {t("list.noReportsInProject")}
                  </div>
                )}
              </Card>
              {!isUnassigned && projectDrafts.length > 1 && (
                <p className="px-1 text-[13px] text-neutral-500">
                  {t("list.reorderHint")}
                </p>
              )}
            </div>
          )}

          {activeTab === "ia" && project && isAdmin && (
            <ProjectIAPane project={project} drafts={projectDrafts} />
          )}

          {activeTab === "data" && project && (
            <div className="space-y-4">
              {/* One tab style in the product, not three: the same
                  underline `Tabs` the hub itself uses, one size down the
                  hierarchy rather than a second grammar. */}
              <Tabs
                items={[
                  { id: "source", label: t("projects.data.sub.source") },
                  {
                    id: "dimensions",
                    label: t("projects.data.sub.dimensions"),
                  },
                ]}
                activeId={dataView}
                onChange={(id: string) => setDataView(id as DataView)}
                variant="underline"
                size="sm"
                aria-label={t("projects.data.sub.ariaLabel")}
              />
              {dataView === "source" ? (
                <>
                  {/* Track B1 — connections first: a data source that
                      needs credentials cannot be saved until one
                      exists. */}
                  <ProjectConnectionsPanel
                    project={project}
                    onChanged={() => setConnectionsVersion((n) => n + 1)}
                  />
                  <Card variant="bordered">
                    <CardBody>
                      <ProjectDataFields
                        project={project}
                        onSaved={setProject}
                        connectionsVersion={connectionsVersion}
                      />
                    </CardBody>
                  </Card>
                </>
              ) : (
                <ProjectDimensionsPane project={project} />
              )}
            </div>
          )}

          {/* LoaderScheduleFields ya trae sus propias Cards (Schedule /
              Run now / Recent jobs) — sin wrapper para evitar
              card-dentro-de-card. */}
          {activeTab === "loader" && project && (
            <LoaderScheduleFields project={project} />
          )}

          {activeTab === "delivery" && project && (
            <DeliverySubscriptionsPanel
              key={project.id}
              projectSlug={project.slug}
              reports={projectDrafts.map((d) => ({
                key: d.key,
                // resolveLocalized, NOT a cast. ``display_name`` is a
                // DisplayLabel: since the iter-16 i18n migration it is
                // routinely ``{en, es}``, and React throws #31 ("objects
                // are not valid as a React child") the moment one reaches
                // JSX. The old ``as string`` silenced the COMPILER
                // without changing the VALUE, so tsc stayed green while
                // the Deliveries tab rendered a blank page.
                //
                // ``||`` and not ``??``: the object is truthy, so ``??``
                // would have kept it even if it had been reached.
                name:
                  resolveLocalized(
                    d.report_config?.display_name as DisplayLabel | undefined,
                    locale,
                  ) || d.key,
              }))}
            />
          )}

          {activeTab === "embed" && project && isAdmin && (
            <ProjectEmbedPane
              key={project.id}
              project={project}
              drafts={projectDrafts}
            />
          )}

          {activeTab === "access" && project && isAdmin && (
            <ProjectAccessPane slug={project.slug} />
          )}

          {activeTab === "config" && project && (
            <div className="mb-4">
              {/* Tabs or a list (2026-10-25): its own action, outside the
                  form's single save. */}
              <ProjectModeCard key={project.id} project={project} onChanged={() => void load()} />
            </div>
          )}
          {activeTab === "config" && project && (
            /* Keyed by project id: the route component stays mounted
               when navigating hub→hub, and the form's useState seeds
               only on mount — without the key, project B would show
               project A's values. */
            <ProjectConfigForm
              key={project.id}
              project={project}
              onSaved={(fresh, slugChanged) => {
                setProject(fresh);
                if (slugChanged) {
                  navigate(`/projects/${encodeURIComponent(fresh.slug)}?tab=config`, {
                    replace: true,
                  });
                }
              }}
            />
          )}
        </div>
      </div>

      <CreateDraftModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        projects={projects}
        presetProjectId={isUnassigned ? null : (project?.id ?? null)}
      />
    </div>
  );
}

// ── Configuración: General + Maestros + Archivos, un solo save ──────

interface ProjectConfigFormProps {
  project: Project;
  onSaved: (fresh: Project, slugChanged: boolean) => void;
}

function ProjectConfigForm({
  project,
  onSaved,
}: ProjectConfigFormProps): JSX.Element {
  const { t } = useT();

  const [slug, setSlug] = useState(project.slug);
  const [name, setName] = useState<DisplayLabel>(
    parseProjectName(project.name),
  );
  const [description, setDescription] = useState(project.description ?? "");
  const [color, setColor] = useState(project.color ?? brand.primary.DEFAULT);
  const [icon, setIcon] = useState(project.icon ?? "");
  const [dashboardEnabled, setDashboardEnabled] = useState(
    project.dashboard_enabled ?? true,
  );
  const [maestrosEnabled, setMaestrosEnabled] = useState(
    project.maestros_enabled ?? true,
  );
  const [filesEnabled, setFilesEnabled] = useState(
    project.files_enabled ?? true,
  );
  const [orderIndex, setOrderIndex] = useState(project.order_index);
  const [masterFilesConfig, setMasterFilesConfig] =
    useState<MasterFilesConfig>(project.master_files_config ?? {});
  const [filesConfig, setFilesConfig] = useState<FilesConfig>(
    project.files_config ?? {},
  );
  // The base table's time rule for the AI Home (empty = not offered).
  const [aiHome, setAiHome] = useState(project.ai_home ?? {});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const trimmedSlug = slug.trim();
  const slugValid = SLUG_PATTERN.test(trimmedSlug) && trimmedSlug.length >= 2;
  const hasAnySection = dashboardEnabled || maestrosEnabled || filesEnabled;
  const canSubmit =
    slugValid && hasProjectName(name) && hasAnySection && !submitting;

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);
    setSavedAt(null);
    setSubmitting(true);
    try {
      await projectsApi.update(project.slug, {
        slug: trimmedSlug,
        name: serializeProjectName(name),
        description: description.trim(),
        color: color.trim() || undefined,
        icon,
        dashboard_enabled: dashboardEnabled,
        maestros_enabled: maestrosEnabled,
        files_enabled: filesEnabled,
        order_index: orderIndex,
        master_files_config: masterFilesConfig,
        files_config: filesConfig,
        ai_home: aiHome,
      });
      const fresh = await projectsApi.get(trimmedSlug);
      setSavedAt(Date.now());
      onSaved(fresh, trimmedSlug !== project.slug);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="flex items-center justify-end gap-3">
        {savedAt !== null && (
          <span className="text-xs text-neutral-500">{t("common.saved")}</span>
        )}
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!canSubmit}
          loading={submitting}
          leftIcon={<Save size={14} />}
        >
          {t("common.save")}
        </Button>
      </div>

      {/* PR-1 — "layout de referencia" del prototipo: columna principal
          (General / Maestros / Archivos) + rail derecho (toggles de
          secciones + tarjeta de alcance proyecto-vs-reporte). */}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-4">
          <Card variant="bordered">
            <CardBody className="space-y-4">
              <GeneralFields
                slug={slug}
                slugValid={slugValid}
                onSlug={setSlug}
                name={name}
                onName={setName}
                description={description}
                onDescription={setDescription}
                color={color}
                onColor={setColor}
                icon={icon}
                onIcon={setIcon}
                dashboardEnabled={dashboardEnabled}
                onDashboardEnabled={setDashboardEnabled}
                maestrosEnabled={maestrosEnabled}
                onMaestrosEnabled={setMaestrosEnabled}
                filesEnabled={filesEnabled}
                onFilesEnabled={setFilesEnabled}
                hasAnySection={hasAnySection}
                submitting={submitting}
                isEdit
                showSectionToggles={false}
                orderIndex={orderIndex}
                onOrderIndex={setOrderIndex}
              />
            </CardBody>
          </Card>

          <Card variant="bordered">
            <CardBody className="space-y-3">
              <h3 className="text-sm font-semibold text-neutral-900">
                {t("projects.form.tab.maestros")}
              </h3>
              <MaestrosConfigFields
                config={masterFilesConfig}
                onChange={setMasterFilesConfig}
                disabled={submitting}
              />
            </CardBody>
          </Card>

          <Card variant="bordered">
            <CardBody className="space-y-3">
              <h3 className="text-sm font-semibold text-neutral-900">
                {t("projects.form.tab.archivos")}
              </h3>
              <ArchivosConfigFields
                config={filesConfig}
                onChange={setFilesConfig}
                disabled={submitting}
              />
            </CardBody>
          </Card>

          <AiHomeTimeEditor
            time={aiHome.time}
            onChange={(time) => setAiHome(time === undefined ? {} : { time })}
            title={t("editor.ai.aiHomeTime.baseTitle")}
            description={t("editor.ai.aiHomeTime.baseDescription")}
            missing={null}
            suggestedTable={`project_raw_${project.slug.replace(/-/g, "_")}`}
          />
        </div>

        <div className="space-y-4">
          <Card variant="bordered">
            <CardBody>
              <SectionToggles
                dashboardEnabled={dashboardEnabled}
                onDashboardEnabled={setDashboardEnabled}
                maestrosEnabled={maestrosEnabled}
                onMaestrosEnabled={setMaestrosEnabled}
                filesEnabled={filesEnabled}
                onFilesEnabled={setFilesEnabled}
                hasAnySection={hasAnySection}
                submitting={submitting}
              />
            </CardBody>
          </Card>

          <Card variant="bordered">
            <CardBody>
              <h3 className="text-sm font-semibold text-neutral-900">
                {t("hub.scope.title")}
              </h3>
              <dl className="mt-2 space-y-2 text-xs">
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-primary">
                    {t("hub.scope.projectLabel")}
                  </dt>
                  <dd className="mt-0.5 text-neutral-600">
                    {t("hub.scope.projectItems")}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-accent-dark">
                    {t("hub.scope.reportLabel")}
                  </dt>
                  <dd className="mt-0.5 text-neutral-600">
                    {t("hub.scope.reportItems")}
                  </dd>
                </div>
              </dl>
            </CardBody>
          </Card>
        </div>
      </div>

      {submitError && (
        <div
          role="alert"
          className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark"
        >
          {submitError}
        </div>
      )}
    </form>
  );
}

// ── Accesos: quién ve este proyecto (PR-3, admin-only) ──────────────

function ProjectAccessPane({ slug }: { slug: string }): JSX.Element {
  const { t } = useT();
  const navigate = useNavigate();

  const [data, setData] = useState<ProjectAccessResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await adminPermissionsApi.getProjectAccess(slug));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Card variant="bordered" noPadding>
        <div className="flex items-center justify-center gap-2 px-6 py-12 text-sm text-neutral-500">
          <Spinner size={16} ariaLabel={t("common.loading")} />
          {t("common.loading")}
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card variant="bordered" className="border-error/40 bg-error/5">
        <CardBody>
          <div className="flex items-start gap-3 text-sm text-error-dark">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">{error ?? t("common.error")}</p>
              <Button
                variant="danger"
                ghost
                size="xs"
                onClick={() => void load()}
                className="mt-3"
              >
                {t("common.retry")}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  const totalReports = data.report_keys.length;

  const totalReportsLabel = totalReports;

  // The role arrives as the backend's own identifier. It goes through the
  // dictionary so an unexpected value shows up as a defect instead of
  // being rendered as if it were a label.
  const roleDict = {
    admin: { label: t("hub.access.role.admin"), tone: "info" as const },
    user: { label: t("hub.access.role.user"), tone: "neutral" as const },
  };

  const columns: Column<(typeof data.users)[number]>[] = [
    {
      id: "user",
      header: t("hub.access.user"),
      cell: (u) => (
        <div>
          <div className="font-medium text-neutral-900">{u.name}</div>
          <div className="text-[13px] text-neutral-500">{u.email}</div>
        </div>
      ),
    },
    {
      id: "role",
      header: t("hub.access.role"),
      cell: (u) => {
        const st = resolveStatus(u.role, roleDict, t("common.missingLabel"));
        return (
          <StatusChip
            label={st.label}
            tone={st.tone}
            unknown={st.unknown}
            title={st.title}
          />
        );
      },
    },
    {
      id: "reports",
      header: t("hub.access.reports"),
      cell: (u) => (
        <span className="text-neutral-600">
          {t("hub.access.reportsOf", {
            count: u.subscribed_keys.length,
            total: totalReportsLabel,
          })}
        </span>
      ),
    },
    {
      id: "overrides",
      header: t("hub.access.overrides"),
      cell: (u) =>
        u.override_count > 0 ? (
          <StatusChip
            label={String(u.override_count)}
            tone="warning"
          />
        ) : (
          <span className="text-[13px] text-neutral-500">—</span>
        ),
    },
  ];

  return (
    <Section title={t("hub.access.title")} flush>
      <DataTable
        columns={columns}
        rows={data.users}
        rowKey={(u) => u.id}
        actions={[
          {
            id: "permissions",
            label: t("hub.access.edit"),
            // Plan J J3: the per-person permission matrix is gone —
            // access is written on a GROUP now. The person's own screen
            // is where their groups and their effective scope live.
            // Retargeted here rather than left to the redirect at
            // App.tsx: /admin/users/:userId/permissions redirects to the
            // people LIST (an inline <Navigate> cannot carry the id, and
            // a redirect component would count as a live route needing
            // its own help topic), so leaving this link alone would drop
            // the person on the way.
            onSelect: (u) =>
              navigate(`/admin/access/people/${encodeURIComponent(u.id)}`),
          },
        ]}
        empty={
          <EmptyState icon={<Users size={24} />} title={t("hub.access.empty")} />
        }
        footnote={t("hub.access.adminNote")}
        className="px-1 pb-4"
      />
    </Section>
  );
}
